import { clsx } from 'clsx';
import { useLocalization } from '@/hooks/use-localization';

type VideoControlsProps = {
    isPlaying: boolean;
    isMuted: boolean;
    onTogglePlay: () => void;
    onToggleMute: () => void;
    className?: string;
};

// Icons sourced from Lucide (https://lucide.dev), MIT licensed. Pasted as inline
// SVG to match the project's no-icon-library convention.
const PlayIcon = () => (
    <svg
        aria-hidden="true"
        focusable="false"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
    </svg>
);

const PauseIcon = () => (
    <svg
        aria-hidden="true"
        focusable="false"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="14" y="3" width="5" height="18" rx="1" />
        <rect x="5" y="3" width="5" height="18" rx="1" />
    </svg>
);

const SoundOnIcon = () => (
    <svg
        aria-hidden="true"
        focusable="false"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
        <path d="M16 9a5 5 0 0 1 0 6" />
        <path d="M19.364 18.364a9 9 0 0 0 0-12.728" />
    </svg>
);

const SoundOffIcon = () => (
    <svg
        aria-hidden="true"
        focusable="false"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />
        <line x1="22" x2="16" y1="9" y2="15" />
        <line x1="16" x2="22" y1="9" y2="15" />
    </svg>
);

/**
 * Accessible play/pause + mute/unmute controls for VideoSection.
 *
 * Meets WCAG 2.2:
 * - 2.5.8 Target size: each button is 44x44px (larger than the 24x24 minimum).
 * - 2.4.7 Focus visible: explicit focus-visible ring.
 * - 4.1.2 Name/role/value: `aria-label` describes the action the button will
 *   perform when activated (e.g. "Unmute video" when currently muted). We
 *   intentionally do NOT use `aria-pressed` here — combining action labels
 *   with a pressed toggle state produces contradictory screen-reader output
 *   like "Unmute video, pressed". Action labels are clearer than state labels
 *   for transport-style controls, and the icon change + label change together
 *   give sighted and AT users equivalent feedback after activation.
 * - 2.1.1 Keyboard: native <button> elements — Tab focusable, Enter/Space activated.
 * - 2.2.2 Pause/stop/hide: play/pause is always visible, never hidden on hover.
 */
export const VideoControls = ({ isPlaying, isMuted, onTogglePlay, onToggleMute, className }: VideoControlsProps) => {
    const { localize } = useLocalization();

    const playLabel = localize('video-play', true);
    const pauseLabel = localize('video-pause', true);
    const muteLabel = localize('video-mute', true);
    const unmuteLabel = localize('video-unmute', true);

    const buttonClass = clsx(
        'r-flex r-h-11 r-w-11 r-items-center r-justify-center r-rounded-full',
        'r-bg-white/90 r-text-black r-shadow-md r-transition',
        'hover:r-bg-white focus:r-outline-none focus-visible:r-ring-2 focus-visible:r-ring-white focus-visible:r-ring-offset-2 focus-visible:r-ring-offset-black/40'
    );

    return (
        <div className={clsx('r-flex r-items-center r-gap-2', className)}>
            <button type="button" onClick={onToggleMute} aria-label={isMuted ? unmuteLabel : muteLabel} className={buttonClass}>
                {isMuted ? <SoundOffIcon /> : <SoundOnIcon />}
            </button>
            <button type="button" onClick={onTogglePlay} aria-label={isPlaying ? pauseLabel : playLabel} className={buttonClass}>
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
        </div>
    );
};
